export const HOSTED_PROVIDER_LABELS = {
  groq: "Groq",
  openai: "OpenAI",
  anthropic: "Anthropic",
  gemini: "Gemini",
};

export function getProviderLabel(provider, serverType) {
  if (provider === "openai_compatible" && serverType === "vllm") {
    return "vLLM (OpenAI compatible)";
  }
  return HOSTED_PROVIDER_LABELS[provider] || provider || "Not configured";
}

export function getRuntimeMode(profile) {
  return profile?.provider === "openai_compatible" && profile?.server_type === "vllm"
    ? "local"
    : "hosted";
}

const numberOrNull = (value) => {
  const cleaned = String(value ?? "").trim();
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const integerOrNull = (value) => {
  const cleaned = String(value ?? "").trim();
  if (!cleaned) return 0;
  if (!/^-?\d+$/.test(cleaned)) return null;
  return Number.parseInt(cleaned, 10);
};

export function validateRuntimeDraft(draft) {
  const errors = {};
  const timeout = numberOrNull(draft.requestTimeoutSeconds);
  const retries = integerOrNull(draft.maxRetries);

  if (draft.mode === "hosted") {
    if (!String(draft.provider || "").trim()) {
      errors.provider = "Select a hosted provider.";
    }
  } else {
    const baseUrl = String(draft.baseUrl || "").trim();
    if (!/^https?:\/\/[^/.\s]+|^https?:\/\/[^/\s]+\.[^/\s]+/.test(baseUrl)) {
      errors.baseUrl = "Enter a valid http(s) base URL.";
    }
    if (!String(draft.primaryModel || "").trim()) {
      errors.primaryModel = "Select a primary model.";
    }
    if (!String(draft.fastModel || "").trim()) {
      errors.fastModel = "Select a fast model.";
    }
  }

  if (timeout !== null && timeout <= 0) {
    errors.requestTimeoutSeconds = "Timeout must be greater than 0.";
  }
  if (retries === null) {
    errors.maxRetries = "Retries must be a whole number.";
  } else if (retries < 0) {
    errors.maxRetries = "Retries must be 0 or greater.";
  }

  return errors;
}

function buildCredentialObject(credential) {
  const apiKey = String(credential?.apiKey || "").trim();
  const displayName = String(credential?.displayName || "").trim();
  if (!apiKey && !displayName) return null;
  const payload = {};
  if (apiKey) payload.api_key = apiKey;
  if (displayName) payload.display_name = displayName;
  return payload;
}

export function buildDraftPayload(draft, credential = null) {
  const payload = {
    provider: draft.mode === "local" ? "openai_compatible" : String(draft.provider || "").trim(),
    request_timeout_seconds: numberOrNull(draft.requestTimeoutSeconds),
    max_retries: integerOrNull(draft.maxRetries) ?? 0,
  };

  if (draft.mode === "local") {
    payload.server_type = draft.serviceType || "vllm";
    payload.primary_model = String(draft.primaryModel || "").trim();
    payload.fast_model = String(draft.fastModel || "").trim();
    payload.base_url = String(draft.baseUrl || "").trim();
  }

  const credentialPayload = buildCredentialObject(credential);
  if (credentialPayload) {
    payload.credential = credentialPayload;
  }

  return payload;
}

export function profileToDraft(profile) {
  const mode = getRuntimeMode(profile);
  return {
    mode,
    provider: mode === "local" ? "openai_compatible" : profile?.provider || "groq",
    serviceType: profile?.server_type || "vllm",
    baseUrl: profile?.base_url || "",
    primaryModel: profile?.primary_model || "",
    fastModel: profile?.fast_model || "",
    requestTimeoutSeconds: String(profile?.request_timeout_seconds ?? (mode === "local" ? 120 : 60)),
    maxRetries: String(profile?.max_retries ?? 2),
  };
}

export function profileToCredential(profile) {
  return {
    displayName: profile?.credential?.display_name || "",
    apiKey: "",
  };
}

export function validateCredentialDraft(draft) {
  const errors = {};
  if (!String(draft.apiKey || "").trim()) {
    errors.apiKey = "API key is required.";
  }
  return errors;
}

export function buildCredentialPayload(draft) {
  const displayName = String(draft.displayName || "").trim();
  const payload = {
    provider: draft.mode === "local" ? "openai_compatible" : String(draft.provider || "").trim(),
    api_key: String(draft.apiKey || "").trim(),
  };
  if (draft.mode === "local") {
    payload.server_type = draft.serviceType || "vllm";
  }
  if (displayName) {
    payload.display_name = displayName;
  }
  return payload;
}
